import { useCallback, useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { Msg } from "../../../types/chat";

export const MESSAGE_PAGE_SIZE = 30;

const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

/**
 * Scroll helpers and "load previous messages" paging for the open channel: the per-channel
 * has-more / loading flags, the earliest-message cursor, and the flags that stop auto-scroll
 * from fighting a prepend.
 */
export function useMessagePaging({
  currentVoidId,
  currentChannelId,
  token,
  stripSelfSeen,
  setMsgsByKey,
  listRef,
  keyboardInset,
}: {
  currentVoidId: string;
  currentChannelId: string;
  token: string;
  stripSelfSeen: (m: Msg) => Msg;
  setMsgsByKey: Dispatch<SetStateAction<Record<string, Msg[]>>>;
  listRef: RefObject<HTMLDivElement | null>;
  keyboardInset: number;
}) {
  const [hasMoreByChan, setHasMoreByChan] = useState<Record<string, boolean>>({});
  const [loadingMoreByChan, setLoadingMoreByChan] = useState<Record<string, boolean>>({});
  const earliestMsgIdRef = useRef<Record<string, string | null>>({});
  const isPrependingRef = useRef(false);
  const loadingMoreRef = useRef<Record<string, boolean>>({});
  const skipAutoScrollRef = useRef(false);
  const loadMoreAbortRef = useRef<AbortController | null>(null);
  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const el2 = listRef.current;
      if (el2) el2.scrollTop = el2.scrollHeight;
    });
  }, [listRef]);
  const loadOlderMessages = useCallback(async () => {
    const cid = fq(currentVoidId, currentChannelId);
    const cursor = earliestMsgIdRef.current[cid];
    if (!cid || !cursor) return;
    if (loadingMoreRef.current[cid]) return;
    loadingMoreRef.current[cid] = true;
    setLoadingMoreByChan(prev => ({ ...prev, [cid]: true }));
    isPrependingRef.current = true;
    skipAutoScrollRef.current = true;
    const prevHeight = listRef.current?.scrollHeight ?? null;
    const controller = new AbortController();
    loadMoreAbortRef.current = controller;
    try {
      const res = await fetch(`/api/channels/history?channelId=${encodeURIComponent(cid)}&before=${encodeURIComponent(cursor)}&limit=${MESSAGE_PAGE_SIZE}`, {
        signal: controller.signal,
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      if (!res.ok) throw new Error('history failed');
      const data = await res.json();
      const older: Msg[] = Array.isArray(data?.messages) ? data.messages.map(stripSelfSeen) : [];
      if (older.length > 0) {
        earliestMsgIdRef.current[cid] = older[0]?.id || cursor;
      }
      setMsgsByKey(old => {
        const existing = old[cid] || [];
        return { ...old, [cid]: [...older, ...existing] };
      });
      setHasMoreByChan(prev => ({ ...prev, [cid]: older.length >= MESSAGE_PAGE_SIZE }));
      requestAnimationFrame(() => {
        const el = listRef.current;
        if (el && prevHeight !== null) {
          const newHeight = el.scrollHeight;
          el.scrollTop = newHeight - prevHeight + el.scrollTop;
        }
      });
    } catch {
      setHasMoreByChan(prev => ({ ...prev, [cid]: false }));
    } finally {
      loadingMoreRef.current[cid] = false;
      setLoadingMoreByChan(prev => { const next = { ...prev }; delete next[cid]; return next; });
      // defer clearing the prepend flags until after layout settles to avoid auto-scroll
      requestAnimationFrame(() => {
        isPrependingRef.current = false;
        setTimeout(() => { skipAutoScrollRef.current = false; }, 80);
      });
    }
  }, [currentVoidId, currentChannelId, token, stripSelfSeen, listRef, setMsgsByKey]);
  // When the keyboard opens and the viewport shrinks, keep the most recent messages in view
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (isPrependingRef.current || skipAutoScrollRef.current) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 320) {
      requestAnimationFrame(() => scrollToBottom());
      setTimeout(() => scrollToBottom(), 50);
    }
  }, [keyboardInset, scrollToBottom, listRef]);
  useEffect(() => {
    return () => {
      loadMoreAbortRef.current?.abort();
      loadMoreAbortRef.current = null;
    };
  }, [currentVoidId, currentChannelId]);
  return {
    scrollToBottom,
    loadOlderMessages,
    hasMoreByChan,
    setHasMoreByChan,
    loadingMoreByChan,
    setLoadingMoreByChan,
    earliestMsgIdRef,
    isPrependingRef,
    skipAutoScrollRef,
  };
}
